/**
 * @fileoverview 轮询下载导出Hook
 * @author yingongjing
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-01-27 10:00:00
 */
import { useCallback, useRef, useEffect } from 'react'
import { App } from 'antd'
import fetch from 'isomorphic-fetch'

/**
 * 下载配置接口
 */
export interface XCDownloadConfig {
  /** 导出URL */
  exportURL: string
  /** 文件名 */
  fileName?: string
  /** 默认文件名 */
  defaultFileName?: string
  /** 最大等待时间（毫秒），默认60秒 */
  maxWaitTime?: number
  /** 轮询间隔（毫秒），默认2秒 */
  pollingInterval?: number
}

/**
 * 下载结果接口
 */
export interface XCDownloadResult {
  /** 是否成功 */
  success: boolean
  /** 错误信息 */
  error?: string
}

/**
 * 下载导出Hook
 * 封装文件下载的完整流程，包括状态检查、内容获取和错误处理
 */
export function usePollAndDownload() {
  const { message } = App.useApp()
  // 轮询定时器引用
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startTimeRef = useRef<number>(0)

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  /**
   * 轮询检查文件是否准备就绪并下载
   * @param config 下载配置
   * @returns Promise<XCDownloadResult>
   */
  const pollAndDownload = useCallback(async (config: XCDownloadConfig): Promise<
    XCDownloadResult
  > => {
    const {
      exportURL,
      fileName,
      defaultFileName = 'download_file',
      maxWaitTime = 60 * 1000,
      pollingInterval = 2000
    } = config

    return new Promise((resolve) => {
      const checkFileStatus = async () => {
        try {
          // 检查是否超过最大等待时间
          const currentTime = Date.now()
          if (currentTime - startTimeRef.current > maxWaitTime) {
            // 清除定时器
            if (timerRef.current) {
              clearTimeout(timerRef.current)
              timerRef.current = null
            }
            const errorMsg = '文件生成超时，请稍后重试'
            message.error(errorMsg)
            resolve({ success: false, error: errorMsg })
            return
          }

          // 先用 HEAD 请求检查文件状态
          const headResponse = await fetch(exportURL, {
            method: 'HEAD'
          })

          if (headResponse.status === 200) {
            // 清除定时器
            if (timerRef.current) {
              clearTimeout(timerRef.current)
              timerRef.current = null
            }

            try {
              // 文件准备好后，用 GET 请求下载实际内容
              const downloadResponse = await fetch(exportURL, {
                method: 'GET'
              })

              if (downloadResponse.ok) {
                const blob = await downloadResponse.blob()
                const blobUrl = window.URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = blobUrl
                const finalFileName = fileName || defaultFileName
                link.download = finalFileName
                link.style.display = 'none'

                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                // 清理 blob URL
                window.URL.revokeObjectURL(blobUrl)

                resolve({ success: true })
              } else {
                const errorMsg = '下载文件失败'
                message.error(errorMsg)
                resolve({ success: false, error: errorMsg })
              }
            } catch (downloadError) {
              const errorMsg = `下载过程中发生错误: ${
                downloadError instanceof Error
                  ? downloadError.message
                  : '未知错误'
              }`
              message.error(errorMsg)
              resolve({ success: false, error: errorMsg })
            }
          } else {
            // 文件未准备好，继续轮询
            timerRef.current = setTimeout(checkFileStatus, pollingInterval)
          }
        } catch (error) {
          console.error('检查文件状态失败:', error)
          // 出错时也继续轮询，但要检查超时
          timerRef.current = setTimeout(checkFileStatus, pollingInterval)
        }
      }

      // 记录开始时间
      startTimeRef.current = Date.now()
      // 开始检查文件状态
      checkFileStatus()
    })
  }, [])

  return {
    pollAndDownload
  }
}

export default usePollAndDownload
